// The Intake review queue under Company (N-094, "forward and file") —
// OWNER/DIRECTOR only, both by the server (a MANAGER's GET 403s) and by the
// tab itself never rendering for a manager (Company.tsx gates the tab the
// same way it gates Management).
//
// Each item's classifier guess is a HINT, not a constraint (owner ruling,
// see the spec this page implements): a human can file ANY item as either a
// license or a Communication, regardless of what the classifier guessed.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useWrite } from './MoneyForms';
import { useResource, refetchResource } from '../lib/store';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import {
  INTAKE_KEY, fetchIntake, confirmIntakeItem, dismissIntakeItem, humanizeIntakeReason,
  type IntakeItem, type IntakeData,
} from '../lib/intake';

type LicenseFieldKey = 'kind' | 'issuer' | 'number' | 'issuedOn' | 'expiresOn' | 'holder';

const LICENSE_FIELD_KEYS: LicenseFieldKey[] = ['kind', 'issuer', 'number', 'issuedOn', 'expiresOn', 'holder'];

const LICENSE_FIELD_LABELS: Record<LicenseFieldKey, string> = {
  kind: 'Kind',
  issuer: 'Issuer',
  number: 'Number',
  issuedOn: 'Issued (YYYY-MM-DD)',
  expiresOn: 'Expires (YYYY-MM-DD)',
  holder: 'Holder',
};

function isLocationCode(v: string | null): v is LocationCode {
  return Boolean(v) && (LOCATION_ORDER as readonly string[]).includes(v as string);
}

export function CompanyIntake(): JSX.Element {
  const load = useResource<IntakeData>(INTAKE_KEY, fetchIntake);

  if (load.status === 'loading') return <EmptyState label="Loading intake…" />;
  if (load.status === 'fault') return <FaultState name="intake_load_failed" detail={load.error} />;

  const items = load.data.items;

  return (
    <div class="card" data-testid="company-intake">
      <div class="section-head">
        <h2>Intake</h2>
        <p>Forwarded email the classifier could not file on its own. Confirm where it goes, or dismiss it.</p>
      </div>
      {items.length === 0 ? (
        <p class="empty">Nothing waiting on review.</p>
      ) : (
        <div class="queue" data-testid="intake-list">
          {items.map((item) => <IntakeRow key={item.id} item={item} />)}
        </div>
      )}
    </div>
  );
}

function IntakeRow({ item }: { item: IntakeItem }): JSX.Element | null {
  const { busy, error, run } = useWrite();
  const [dismissing, setDismissing] = useState(false);
  const [kind, setKind] = useState<'license' | 'communication'>(
    item.guessedKind === 'license' ? 'license' : 'communication',
  );
  const [locationCode, setLocationCode] = useState<string>(
    isLocationCode(item.guessedClinic) ? item.guessedClinic : '',
  );
  const [fields, setFields] = useState<Record<LicenseFieldKey, string>>(() => {
    const guessed = item.guessedFields || {};
    const out = {} as Record<LicenseFieldKey, string>;
    for (const k of LICENSE_FIELD_KEYS) {
      const v = guessed[k];
      out[k] = v == null ? '' : String(v);
    }
    return out;
  });
  const [warning, setWarning] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  async function reload(): Promise<void> {
    await refetchResource(INTAKE_KEY, fetchIntake);
  }

  async function confirm(): Promise<void> {
    if (kind === 'license' && !locationCode) return;
    const body = kind === 'license'
      ? {
        kind,
        locationCode,
        fields: Object.fromEntries(
          LICENSE_FIELD_KEYS.map((k) => [k, fields[k].trim()]).filter(([, v]) => v !== ''),
        ),
      }
      : { kind: 'communication' as const };
    const result = await confirmIntakeItem(item.id, body);
    if (result.message) setWarning(result.message);
    setGone(true);
    await reload();
  }

  async function dismiss(): Promise<void> {
    await dismissIntakeItem(item.id);
    setGone(true);
    await reload();
  }

  // Optimistic: hide immediately on success while the refetch above
  // reconciles the list against the server (ServiceCompliance.tsx's own
  // success-then-refetch idiom, adapted — this component removes the row
  // itself rather than the parent tracking a set of hidden ids, since the
  // parent's next render from the refetched list will drop it anyway).
  if (gone) return null;

  return (
    <div class="q intake-item" data-testid={`intake-item-${item.id}`}>
      <div class="code">{(item.guessedKind ?? 'unknown').toUpperCase()}</div>
      <div>
        <b>{item.subject || '(no subject)'}</b>
        <span>
          {item.fromAddress || 'Unknown sender'}
          {item.confidence != null ? ` · ${Math.round(item.confidence * 100)}% confidence` : ''}
          {' · '}
          {humanizeIntakeReason(item.reason)}
          {item.guessedClinic ? ` · guessed ${LOCATION_NAMES[item.guessedClinic as LocationCode] ?? item.guessedClinic}` : ''}
        </span>
        {item.emailLink ? (
          <span><a href={item.emailLink} target="_blank" rel="noreferrer">View original email</a></span>
        ) : null}
        {Object.keys(item.guessedFields || {}).length > 0 ? (
          <span class="muted">
            Guessed: {Object.entries(item.guessedFields).map(([k, v]) => `${k}: ${String(v)}`).join(', ')}
          </span>
        ) : null}

        <div class="raise-decision-form">
          <label>
            File as
            <select
              aria-label={`File "${item.subject ?? 'this item'}" as`}
              value={kind}
              onChange={(e) => setKind((e.target as HTMLSelectElement).value as 'license' | 'communication')}
            >
              <option value="communication">Communication</option>
              <option value="license">License</option>
            </select>
          </label>
          {kind === 'license' ? (
            <>
              <label>
                Clinic
                <select
                  aria-label={`Clinic for "${item.subject ?? 'this item'}"`}
                  value={locationCode}
                  onChange={(e) => setLocationCode((e.target as HTMLSelectElement).value)}
                >
                  <option value="">Select…</option>
                  {LOCATION_ORDER.map((code) => (
                    <option key={code} value={code}>{LOCATION_NAMES[code]}</option>
                  ))}
                </select>
              </label>
              {LICENSE_FIELD_KEYS.map((k) => (
                <label key={k}>
                  {LICENSE_FIELD_LABELS[k]}
                  <input
                    type="text"
                    aria-label={LICENSE_FIELD_LABELS[k]}
                    value={fields[k]}
                    onInput={(e) => setFields((prev) => ({ ...prev, [k]: (e.target as HTMLInputElement).value }))}
                  />
                </label>
              ))}
            </>
          ) : null}
          <button
            type="button"
            class="btn primary"
            disabled={busy || (kind === 'license' && !locationCode)}
            onClick={() => void run(confirm)}
          >
            {busy ? 'Saving…' : kind === 'license' ? 'Confirm as License' : 'Confirm as Communication'}
          </button>
          {dismissing ? (
            <span data-testid={`intake-dismiss-confirm-${item.id}`}>
              Dismiss?{' '}
              <button type="button" class="btn" disabled={busy} onClick={() => void run(dismiss)}>
                {busy ? 'Dismissing…' : 'Yes'}
              </button>{' '}
              <button type="button" class="btn" disabled={busy} onClick={() => setDismissing(false)}>Cancel</button>
            </span>
          ) : (
            <button type="button" class="btn" disabled={busy} onClick={() => setDismissing(true)}>Dismiss</button>
          )}
          {error ? <p class="bad" role="alert">{error}</p> : null}
          {warning ? <p class="warn" role="alert">{warning}</p> : null}
        </div>
      </div>
    </div>
  );
}
